/**
 * Canvas thumbnail renderer for the Sports Thumbnail Generator.
 *
 * The renderer is a pure function: pass it a match plus its surrounding
 * data (teams, leagues, brand) and it returns a fully-drawn HTMLCanvasElement.
 * The same logic is used by the live preview download button and the batch
 * ZIP exporter.
 *
 * Aspect-ratio rule: NEVER call ctx.drawImage(img, x, y, size, size) for logos
 * — always use drawLogoContain so the source aspect ratio is preserved
 * (matches CSS `object-contain` used in <ThumbnailPreview/>).
 */

import { mediaUrl } from "./api";

const PLATFORM_DIMENSIONS = {
  youtube: { w: 1920, h: 1080 },
  uscreen: { w: 1480, h: 840 },
  "uscreen-vertical": { w: 1188, h: 1682 },
};

export function getDimensions(platform) {
  return PLATFORM_DIMENSIONS[platform] || PLATFORM_DIMENSIONS.uscreen;
}

/**
 * Load an image and return an HTMLImageElement that is safe to draw onto a
 * canvas without tainting it.
 *
 * Why fetch+blob instead of `<img crossOrigin="anonymous">`?
 *   - Logos can be stored as data: URLs (no CORS), absolute http(s) URLs (CORS
 *     varies by host), or `/api/media/{id}/file` references (server CORS is
 *     fine but the browser may have already cached the image WITHOUT the
 *     crossOrigin attribute via the live preview, producing a tainted image
 *     when the canvas exporter re-requests it). Loading via fetch() and
 *     converting to a same-origin `blob:` URL bypasses all of that.
 *   - data: URLs are still loaded directly because they're already inline.
 */
/**
 * Load an image with up to `attempts` retries on network/decode failures.
 * Resolves to an HTMLImageElement on success, or null after all retries.
 * Retries are critical for batch exports where 90+ logo fetches in series can
 * hit transient failures that previously produced thumbnails with team-initial
 * letters instead of logos.
 */
function loadImage(src, attempts = 3, backoffMs = 300) {
  return new Promise((resolve) => {
    if (!src) return resolve(null);

    const isData = src.startsWith("data:");

    const tryOnce = (attemptsLeft) => {
      if (isData) {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null); // data URLs don't get retried
        img.src = src;
        return;
      }

      const onFail = () => {
        if (attemptsLeft > 1) {
          setTimeout(() => tryOnce(attemptsLeft - 1), backoffMs);
        } else {
          resolve(null);
        }
      };

      // First retry uses force-cache; subsequent retries bypass cache to avoid
      // a poisoned cache entry from the original failed fetch.
      const cacheMode = attemptsLeft === attempts ? "force-cache" : "no-cache";

      fetch(src, { credentials: "omit", cache: cacheMode })
        .then((r) => (r.ok ? r.blob() : null))
        .then((blob) => {
          if (!blob) return onFail();
          const objectUrl = URL.createObjectURL(blob);
          const img = new Image();
          img.onload = () => {
            setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
            resolve(img);
          };
          img.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            onFail();
          };
          img.src = objectUrl;
        })
        .catch(onFail);
    };

    tryOnce(attempts);
  });
}

function drawLogoContain(ctx, img, cx, cy, boxSize) {
  if (!img) return;
  const sw = img.naturalWidth || img.width || 1;
  const sh = img.naturalHeight || img.height || 1;
  const ar = sw / sh;
  let drawW;
  let drawH;
  if (ar >= 1) {
    drawW = boxSize;
    drawH = boxSize / ar;
  } else {
    drawH = boxSize;
    drawW = boxSize * ar;
  }
  ctx.drawImage(img, cx - drawW / 2, cy - drawH / 2, drawW, drawH);
}

function drawBackground(ctx, canvas, leftColor, rightColor, isVertical) {
  const gradient = isVertical
    ? ctx.createLinearGradient(0, 0, 0, canvas.height)
    : ctx.createLinearGradient(0, 0, canvas.width, 0);
  gradient.addColorStop(0, leftColor);
  gradient.addColorStop(0.35, leftColor + "dd");
  gradient.addColorStop(0.65, rightColor + "dd");
  gradient.addColorStop(1, rightColor);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // Soft radial highlights for depth
  ctx.globalAlpha = 0.1;
  const stops = isVertical
    ? [
        { x: canvas.width / 2, y: canvas.height * 0.25, r: canvas.width * 0.5 },
        { x: canvas.width / 2, y: canvas.height * 0.75, r: canvas.width * 0.5 },
      ]
    : [
        { x: canvas.width * 0.2, y: canvas.height / 2, r: canvas.width * 0.3 },
        { x: canvas.width * 0.8, y: canvas.height / 2, r: canvas.width * 0.3 },
      ];
  for (const s of stops) {
    const radial = ctx.createRadialGradient(s.x, s.y, 0, s.x, s.y, s.r);
    radial.addColorStop(0, "#ffffff");
    radial.addColorStop(1, "transparent");
    ctx.fillStyle = radial;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }
  ctx.globalAlpha = 1;
}

function drawLogosLayout(ctx, canvas, layoutCtx) {
  const {
    isCompetitionOnly, isVertical,
    team1Logo, team2Logo, leagueLogo,
    team1Name, team2Name, league,
    teamLogoSize, leagueLogoSize, largeLeagueLogoSize,
  } = layoutCtx;

  const drawTextFallback = (text, size, x, y) => {
    ctx.fillStyle = "#ffffff";
    ctx.font = `bold ${size}px Montserrat, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(text, x, y);
  };

  if (isCompetitionOnly) {
    const cx = canvas.width / 2;
    const cy = canvas.height * 0.425;
    if (leagueLogo) drawLogoContain(ctx, leagueLogo, cx, cy, largeLeagueLogoSize);
    else drawTextFallback(league?.name?.[0] || "?", largeLeagueLogoSize * 0.5, cx, cy);
    return;
  }

  if (isVertical) {
    const cx = canvas.width / 2;
    const team1Y = canvas.height * 0.28;
    const leagueY = canvas.height * 0.5;
    const team2Y = canvas.height * 0.72;

    if (team1Logo) drawLogoContain(ctx, team1Logo, cx, team1Y, teamLogoSize);
    else drawTextFallback(team1Name[0], teamLogoSize * 0.6, cx, team1Y);

    if (leagueLogo) drawLogoContain(ctx, leagueLogo, cx, leagueY, leagueLogoSize);

    if (team2Logo) drawLogoContain(ctx, team2Logo, cx, team2Y, teamLogoSize);
    else drawTextFallback(team2Name[0], teamLogoSize * 0.6, cx, team2Y);
    return;
  }

  // Horizontal
  const cy = canvas.height * 0.45;
  const team1X = canvas.width * 0.25;
  const team2X = canvas.width * 0.75;

  if (team1Logo) drawLogoContain(ctx, team1Logo, team1X, cy, teamLogoSize);
  else drawTextFallback(team1Name[0], teamLogoSize * 0.5, team1X, cy);

  if (leagueLogo) drawLogoContain(ctx, leagueLogo, canvas.width / 2, cy, leagueLogoSize);

  if (team2Logo) drawLogoContain(ctx, team2Logo, team2X, cy, teamLogoSize);
  else drawTextFallback(team2Name[0], teamLogoSize * 0.5, team2X, cy);
}

function drawCompetitionBadge(ctx, dimensions, isVertical, text) {
  if (!text) return;
  ctx.fillStyle = "rgba(0, 0, 0, 0.4)";
  ctx.font = `600 ${isVertical ? dimensions.w * 0.03 : dimensions.h * 0.035}px Inter, sans-serif`;
  const textWidth = ctx.measureText(text).width;
  const padding = isVertical ? 15 : 20;
  const height = isVertical ? 25 : 32;
  const x = isVertical ? 20 : 25;
  const y = 20;
  ctx.beginPath();
  ctx.roundRect(x, y, textWidth + padding * 2, height, 4);
  ctx.fill();
  ctx.fillStyle = "#ffffff";
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillText(text, x + padding, y + height / 2);
}

function drawBranding(ctx, canvas, dimensions, isVertical, brand, brandLogoImg) {
  const baseY = canvas.height * 0.91;
  const lineSpacing = isVertical ? dimensions.h * 0.04 : dimensions.h * 0.055;
  const showWatchOnText = brand?.showWatchOnText !== false;

  ctx.fillStyle = "#ffffff";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";

  if (brandLogoImg) {
    if (showWatchOnText) {
      ctx.font = `900 ${isVertical ? dimensions.w * 0.035 : dimensions.h * 0.035}px Montserrat, sans-serif`;
      ctx.fillText("WATCH ON", canvas.width / 2, baseY - lineSpacing);
    }
    const logoBoxH = isVertical ? dimensions.h * 0.06 : dimensions.h * 0.11;
    const logoBoxW = canvas.width * 0.6;
    const sw = brandLogoImg.naturalWidth || brandLogoImg.width || 1;
    const sh = brandLogoImg.naturalHeight || brandLogoImg.height || 1;
    const ar = sw / sh;
    let drawW = logoBoxW;
    let drawH = drawW / ar;
    if (drawH > logoBoxH) {
      drawH = logoBoxH;
      drawW = drawH * ar;
    }
    const cy = showWatchOnText ? baseY + lineSpacing * 0.4 : baseY - lineSpacing * 0.2;
    ctx.drawImage(brandLogoImg, canvas.width / 2 - drawW / 2, cy - drawH / 2, drawW, drawH);
    return;
  }

  const brandName = brand?.name || "New Logical Sports";
  if (showWatchOnText) {
    ctx.font = `900 ${isVertical ? dimensions.w * 0.035 : dimensions.h * 0.035}px Montserrat, sans-serif`;
    ctx.fillText("WATCH ON", canvas.width / 2, baseY - lineSpacing);
  }
  ctx.font = `900 ${isVertical ? dimensions.w * 0.05 : dimensions.h * 0.055}px Montserrat, sans-serif`;
  ctx.fillText(brandName.toUpperCase(), canvas.width / 2, baseY + lineSpacing * 0.4);
}

/**
 * Render a thumbnail to an off-screen canvas.
 * @param {object} match
 * @param {"youtube"|"uscreen"|"uscreen-vertical"} platform
 * @param {object} ctx
 * @param {Array} ctx.teams
 * @param {Array} ctx.leagues
 * @param {object|null} ctx.brand
 * @param {boolean} [ctx.showCompetitionOpt=true]
 * @returns {Promise<HTMLCanvasElement>}
 */
export async function generateThumbnailCanvas(
  match,
  platform,
  { teams, leagues, brand, showCompetitionOpt = true } = {}
) {
  const dimensions = getDimensions(platform);
  const isVertical = platform === "uscreen-vertical";

  const team1 = teams.find((t) => t.id === match?.team1Id);
  const team2 = teams.find((t) => t.id === match?.team2Id);
  const league = leagues.find((l) => l.id === match?.leagueId);

  const team1Name = team1?.name || match?.team1Name || "Team 1";
  const team2Name = team2?.name || match?.team2Name || "Team 2";
  const isTBD = team1Name === "TBD" && team2Name === "TBD";
  const isCompetitionOnly = isTBD && league;

  const leftColor = isCompetitionOnly
    ? (league?.primaryColor || "#1e3a5f")
    : (team1?.primaryColor || league?.primaryColor || "#1e3a5f");
  const rightColor = isCompetitionOnly
    ? (league?.secondaryColor || league?.primaryColor || "#0ea5e9")
    : (team2?.primaryColor || league?.secondaryColor || "#0ea5e9");

  const canvas = document.createElement("canvas");
  canvas.width = dimensions.w;
  canvas.height = dimensions.h;
  const ctx = canvas.getContext("2d");

  drawBackground(ctx, canvas, leftColor, rightColor, isVertical);

  const [team1Logo, team2Logo, leagueLogo, brandLogoImg] = await Promise.all([
    loadImage(mediaUrl(team1?.logo)),
    loadImage(mediaUrl(team2?.logo)),
    loadImage(mediaUrl(league?.logo)),
    loadImage(mediaUrl(brand?.logo)),
  ]);

  // Track which expected logos failed to load so callers (e.g. batch export)
  // can warn the user instead of silently shipping a thumbnail with text
  // initials in place of a real logo.
  const missingLogos = [];
  if (team1?.logo && !team1Logo) missingLogos.push(team1.name || "team1");
  if (team2?.logo && !team2Logo) missingLogos.push(team2.name || "team2");
  if (league?.logo && !leagueLogo) missingLogos.push(league.name || "league");

  const baseSize = isVertical ? Math.min(dimensions.w, dimensions.h) * 0.25 : dimensions.h * 0.5;
  drawLogosLayout(ctx, canvas, {
    isCompetitionOnly, isVertical,
    team1Logo, team2Logo, leagueLogo,
    team1Name, team2Name, league,
    teamLogoSize: baseSize,
    leagueLogoSize: baseSize * 0.35,
    largeLeagueLogoSize: baseSize * 1.4,
  });

  if (showCompetitionOpt && match?.competition) {
    drawCompetitionBadge(ctx, dimensions, isVertical, match.competition);
  }

  drawBranding(ctx, canvas, dimensions, isVertical, brand, brandLogoImg);
  // Attach metadata to the canvas object for callers to inspect.
  canvas.__missingLogos = missingLogos;
  return canvas;
}
